import ApplicationController from './application_controller'

/**
 * Controller that manages cascading copy of form fields.
 * 
 * Flexible system based on chain indices that allows arbitrary length.
 * 
 * Usage example:
 * - Field 1: data-chain-index="0" data-identifier="contact_name" (customer_name)
 * - Field 2: data-chain-index="1" data-identifier="contact_name" (delivery_contact_name) 
 * - Field 3: data-chain-index="2" data-identifier="contact_name" (billing_contact_name)
 * - Field 4: data-chain-index="3" data-identifier="contact_name" (other_contact_name)
 * 
 * Copying happens from index N to index N+1
 * - Copying only happens if the destination field is empty
 * - Copying is triggered on blur (when leaving the field)
 * 
 * Required data attributes:
 * - data-identifier: Common identifier to group fields in the same chain
 * - data-chain-index: Position in the chain (0, 1, 2, ...)
 * - action="blur->copy-field-to-field#copy": Action to trigger the copy
 */
export default class extends ApplicationController {
  static targets = ["field"]

  connect() {
  }

  copy(event) {
    const sourceElement = event.currentTarget
    const identifier = sourceElement.dataset.identifier
    const currentIndex = parseInt(sourceElement.dataset.chainIndex)

    if (!identifier || isNaN(currentIndex)) {
      console.warn('copy-field-to-field: missing identifier or chain-index', sourceElement)
      return
    }

    this.copyToNextInChain(sourceElement, identifier, currentIndex)
  }

  // Private methods

  copyToNextInChain(sourceElement, identifier, currentIndex) {
    const nextElement = this.findFieldByIdentifierAndIndex(identifier, currentIndex + 1)
    
    if (nextElement && !nextElement.value) {
      this.copyValueAndTriggerBlur(sourceElement, nextElement)
    }
  }

  copyChain(fields) {
    // Sort fields by index
    const sortedFields = this.sortFieldsByIndex(fields)
    
    // Copy in cascade from the first non-empty to the following empty ones
    const sourceField = sortedFields.find(field => field.value)
    if (!sourceField) return

    const sourceIndex = parseInt(sourceField.dataset.chainIndex)
    
    sortedFields.forEach(field => {
      const fieldIndex = parseInt(field.dataset.chainIndex)
      if (fieldIndex > sourceIndex && !field.value) {
        this.copyValueAndTriggerBlur(sourceField, field)
      }
    })
  }

  groupFieldsByIdentifier() {
    const groups = {}
    
    this.fieldTargets.forEach(field => {
      const identifier = field.dataset.identifier
      if (identifier) {
        if (!groups[identifier]) {
          groups[identifier] = []
        }
        groups[identifier].push(field)
      }
    })
    
    return groups
  }

  findFieldByIdentifierAndIndex(identifier, index) {
    return this.fieldTargets.find(field => 
      field.dataset.identifier === identifier && 
      parseInt(field.dataset.chainIndex) === index
    )
  }

  sortFieldsByIndex(fields) {
    return fields.sort((a, b) => {
      const indexA = parseInt(a.dataset.chainIndex) || 0
      const indexB = parseInt(b.dataset.chainIndex) || 0
      return indexA - indexB
    })
  }

  copyValueAndTriggerBlur(sourceElement, targetElement) {
    // Programmatic assignment bypasses the target's maxlength, so enforce it here
    const maxLength = targetElement.maxLength
    const value = sourceElement.value
    targetElement.value = maxLength > 0 ? value.slice(0, maxLength) : value
    targetElement.dispatchEvent(new Event('blur', { bubbles: true }))
  }
}
